import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const err = (e, d) => toast.error(e?.response?.data?.detail || d);

const LinkPreview = ({ p, onConfirm, onCancel, busy }) => (
  <div className="mt-2 w-full rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="na-staff-match-preview">
    <div>Link <b>{p.name}</b> to <b data-testid="na-staff-match-preview-employee">{p.employee.name}</b> <span className="text-slate-500 break-all">({p.employee.email})</span>?</div>
    <div className="text-[12px] text-slate-600 mt-1" data-testid="na-staff-match-preview-impact">
      {p.shift_count} shift(s){p.vehicles.length ? ` · vehicle crew: ${p.vehicles.join(", ")}` : ""}
      {p.shifts.slice(0, 5).map((s) => <div key={s.shift_id}>{s.date} {s.start_time || ""} · {s.shift_id}{s.task_legacy_id ? ` · ${s.task_legacy_id}` : ""}</div>)}
    </div>
    <div className="text-[12px] text-slate-500 mt-1">No past notifications are sent. Future changes to these shifts will alert this employee.</div>
    <div className="flex gap-2 mt-2">
      <Button size="sm" onClick={onConfirm} disabled={busy} data-testid="na-staff-match-confirm">Confirm link</Button>
      <Button size="sm" variant="outline" onClick={onCancel} data-testid="na-staff-match-cancel">Cancel</Button>
    </div>
  </div>
);

const Pick = ({ name, staff, onPick, testid }) => (
  <select className="border rounded-md px-2 py-1 text-sm max-w-full" value="" onChange={(e) => e.target.value && onPick(name, e.target.value)} data-testid={testid}>
    <option value="">Link to employee…</option>
    {staff.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
  </select>
);

export const StaffMatchCard = ({ Card }) => {
  const [items, setItems] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [staff, setStaff] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/staff-match-issues").then((r) => { setItems(r.data.items); setStaff(r.data.staff || []); }).catch(() => setItems([]));
    api.get("/staff-aliases").then((r) => setAliases(r.data.items || [])).catch(() => setAliases([]));
  };
  useEffect(() => { load(); }, []);
  const ask = (name, user_id) => api.get("/staff-match-issues/preview", { params: { name, user_id } })
    .then((r) => setPreview({ ...r.data, user_id })).catch((e) => err(e, "Could not load preview"));
  const confirm = async () => {
    setBusy(true);
    try { await api.post("/staff-match-issues/resolve", { name: preview.name, user_id: preview.user_id, confirm: true }); toast.success(`${preview.name} linked`); setPreview(null); load(); }
    catch (e) { err(e, "Could not link"); } finally { setBusy(false); }
  };
  const undo = async (name) => {
    try { await api.delete("/staff-aliases", { params: { name } }); toast.success(`Link for ${name} removed`); load(); }
    catch (e) { err(e, "Could not undo"); }
  };
  const row = (name, body, testid) => (
    <div key={name} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={testid}>
      {body}
      {preview?.name === name && <LinkPreview p={preview} onConfirm={confirm} onCancel={() => setPreview(null)} busy={busy} />}
    </div>
  );
  return (
    <Card title="Unmatched staff names" testid="na-staff-match">
      <p className="text-[12px] text-slate-500 mb-2">Names on shifts or vehicle crews that match no single employee. They get no alerts until an Operations manager links them.</p>
      {!items ? <div className="text-sm text-slate-400">Loading…</div> : !items.length ? (
        <div className="text-sm text-slate-500" data-testid="na-staff-match-empty">All staff names are matched.</div>
      ) : <div className="divide-y divide-slate-100">{items.map((i) => row(i.name, <>
        <span className="font-medium min-w-0 truncate">{i.name}</span>
        <span className="text-[12px] text-slate-500">{i.reason === "ambiguous" ? "matches several people" : i.reason === "unlinked" ? "link was undone" : "no matching employee"} · {i.last_context}{i.count ? ` · seen ${i.count}×` : ""}</span>
        <span className="ml-auto"><Pick name={i.name} staff={staff} onPick={ask} testid={`na-staff-match-select-${i.name}`} /></span>
      </>, `na-staff-match-row-${i.name}`))}</div>}
      {!!aliases.length && <div className="mt-4" data-testid="na-staff-aliases">
        <div className="text-sm font-medium mb-1">Linked names</div>
        <div className="divide-y divide-slate-100">{aliases.map((a) => row(a.name, <>
          <span className="min-w-0 truncate"><b>{a.name}</b> → {a.user_name || a.user_id}</span>
          <span className="text-[12px] text-slate-500" data-testid={`na-staff-alias-approver-${a.name}`}>approved by {a.by_name || a.by} · {a.at ? new Date(a.at).toLocaleString() : ""}</span>
          <span className="ml-auto flex flex-wrap gap-2 max-w-full">
            <Pick name={a.name} staff={staff} onPick={ask} testid={`na-staff-alias-change-${a.name}`} />
            <Button size="sm" variant="outline" onClick={() => undo(a.name)} data-testid={`na-staff-alias-undo-${a.name}`}>Undo</Button>
          </span>
        </>, `na-staff-alias-row-${a.name}`))}</div>
      </div>}
    </Card>
  );
};
